import {
  EventsCategory,
  ExternalDataService,
  LanguageService,
  MediaItem,
  NewsCategory,
  PubMedItem,
  RocheEndpoint,
} from '@repo/roche-xx-shared';
import { getLocalizedDetailedPagePath } from '../../affilate-logic/roche-ch';
import { fetchDataSearchResults } from '../cludo-queries';

const config = {
  MEDIA: {
    path: 'media/releases',
  },
  INVESTOR: {
    path: 'investors/updates',
  },
};

const ALL_SUBTAB = 'all';

export async function fetchPubMedTabs() {
  const categories = await ExternalDataService.getPubMedCategories();

  return ['All', ...categories.functions];
}

export async function fetchPubMedList({ tab, page }) {
  const data = await ExternalDataService.getPubMed({ page: page - 1, size: 10 }, '', tab === 'All' ? '' : tab);

  const pubs: RocheEndpoint<PubMedItem[]> = {
    Page: page,
    Total: data.rowCount,
    Items: data.publications,
  };

  for (const [index, item] of pubs.Items.entries()) {
    pubs.Items[index] = {
      ...item,
    };
  }

  return pubs;
}

export async function fetchPubMedSearch({ tab, query, page }) {
  const data = await ExternalDataService.getPubMed({ page: page - 1, size: 10 }, query, tab === 'All' ? '' : tab);

  const pubs: RocheEndpoint<PubMedItem[]> = {
    Page: page,
    Total: data.rowCount,
    Items: data.publications,
  };

  for (const [index, item] of pubs.Items.entries()) {
    pubs.Items[index] = {
      ...item,
    };
  }

  return pubs;
}

export async function fetchInvestorEventsTabs() {
  const events = await ExternalDataService.getEventYears({
    category: EventsCategory.INVESTOR,
  });

  events.unshift('upcoming');

  return events;
}

export async function fetchInvestorEventsSubtabs() {
  const events = await ExternalDataService.getEventTags({
    category: EventsCategory.INVESTOR,
  });

  if (events.length === 0) {
    return [];
  }

  events.unshift(ALL_SUBTAB);

  return events;
}

export async function fetchInvestorEventsList({ tab, subtab, page }) {
  return fetchEventsList({ tab, subtab, page, category: EventsCategory.INVESTOR });
}

export async function fetchEventsTabs() {
  const events = await ExternalDataService.getEventYears({
    category: EventsCategory.MEDIA,
  });

  events.unshift('upcoming');

  return events;
}

export async function fetchEventsList({ tab, subtab, page, category = EventsCategory.MEDIA }) {
  const events =
    tab === 'upcoming'
      ? await ExternalDataService.getUpcomingEvents({ category, tag: subtab ?? ALL_SUBTAB, page })
      : await ExternalDataService.getEvents({ category, year: tab, tag: subtab ?? ALL_SUBTAB, page });

  for (const [index, item] of events.Items.entries()) {
    events.Items[index] = {
      ...item,
    };
  }

  return events;
}

export function fetchSwissNewsTabs() {
  return ExternalDataService.getNewsYears({ category: NewsCategory.SWISS });
}

export function fetchSwissNewsList(args) {
  return fetchNewsList({ ...args, category: NewsCategory.SWISS });
}

export function fetchInvestorUpdatesList(args) {
  return fetchNewsList({ ...args, category: NewsCategory.INVESTOR });
}

export function fetchInvestorUpdatesSearch(args) {
  return fetchNewsSearch({ ...args, type: 'investors' });
}
export function fetchSwissMediaSearch(args) {
  return fetchNewsSearch({ ...args, type: 'swiss' });
}

export function fetchNewsTabs() {
  return ExternalDataService.getNewsYears({ category: NewsCategory.MEDIA });
}

export function fetchInvestorTabs() {
  return ExternalDataService.getNewsYears({ category: NewsCategory.INVESTOR });
}

export async function fetchNewsList({ tab, page, category = NewsCategory.MEDIA }) {
  const result = await ExternalDataService.getNews({ category, year: tab, page });
  result.Items = await extendNewsItemsData(result.Items);

  return result;
}

/**
 * Fetches news search results using Cludo
 *
 * @param params - Search parameters including query, page, and type
 * @returns Transformed search results in RocheEndpoint format
 */
export async function fetchNewsSearch({ query, page, type = NewsCategory.MEDIA }) {
  return fetchDataSearchResults({
    query,
    page,
    type,
    perPage: 10,
  }) as Promise<RocheEndpoint<Partial<MediaItem>[]>>;
}

export function fetchAdhocTabs() {
  return ExternalDataService.getAdhocYears();
}

export async function fetchAdhocList({ tab, page }) {
  const result = await ExternalDataService.getAdhoc({ year: tab, page });
  result.Items = await extendNewsItemsData(result.Items);

  return result;
}

async function extendNewsItemsData(items: MediaItem[]) {
  const locale = LanguageService.getActiveLanguage();
  const extendedItems = [];

  for (const [index, item] of items.entries()) {
    let link = '';
    let path = '';

    if (item.Category !== 'SWISS') {
      link = LanguageService.getLocaleAwareLink(`${config[item.Category].path}/${item.Slug}`, locale);
      path = `${config[item.Category].path}`;
    } else {
      // See https://roche-ds.atlassian.net/browse/EXTCWP-550:
      // if the item has only one language, we link directly to that item in the language of the item.
      // if we have multiple languages, but the current language is not available, we link to the item in the first
      // available language.
      const itemLanguages = Object.keys(item.Title);
      let localeOverride = locale;
      if (itemLanguages.length === 1 || !itemLanguages.includes(locale)) {
        // if the item contains the default locale, we use it as the override
        if (itemLanguages.includes(LanguageService.getDefaultLocale())) {
          localeOverride = LanguageService.getDefaultLocale();
        } else {
          // otherwise we use the first available language
          localeOverride = itemLanguages[0];
        }
      }
      link = LanguageService.getLocaleAwareLink(
        `/${getLocalizedDetailedPagePath(localeOverride)}/informationen/${item.Slug}`,
        localeOverride,
      );
    }

    extendedItems[index] = {
      ...item,
      localisedTitle: ExternalDataService.getLocalisedValue(
        item as unknown as Record<string, unknown>,
        'Title',
        locale,
      ),
      link,
      path,
    };
  }

  return extendedItems;
}
